import { useEffect, useMemo, useState } from "react";
import { useSortBy, useTable } from "react-table";
import NewCloseSVG from "../../../../assets/svgs/new-close.svg";
import axios from "../../../../axios";
import { toDisplayTime } from "../../../../utilis/date-formats";
import styles from "./ResidentDetails.module.scss";
import HtmlTooltip from "../../../shared/HtmlTooltip";
import CustomTooltipContentWithAction, { richTooltipSlotProps } from "../../../shared/CustomTooltipContentWithAction";

function Table({ columns, data, setSelectedResident }) {
	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setSortBy } = useTable(
		{
			columns,
			data,
		},
		useSortBy
	);

	return (
		<div className={styles.tableWrpr}>
			<table {...getTableProps()}>
				<thead className={`ffmar fs12 ttuc`}>
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th
									{...column.getHeaderProps(column.getSortByToggleProps())}
									{...(column.isSortable
										? {
											onClick: () => {
												//set sort desc, aesc or none?
												const desc =
													column.isSortedDesc === true ? undefined : column.isSortedDesc === false ? true : false;
												setSortBy([
													{ id: column.id, desc },
													{ id: "createdAt", desc },
												]);
											},
										}
										: {})}
								>
									{column.render("Header")}
									<span>
										{column.isSorted
											? column.isSortedDesc
												? // ? " 🔽"
												// : " 🔼"
												" 🔽"
												: " 🔼"
											: ""}
									</span>
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{rows.map((row, i) => {
						prepareRow(row);
						return (
							<tr
								className={`ffmar fs15`}
								{...row.getRowProps()}
								onClick={() => setSelectedResident?.(row.original)}
							>
								{row.cells.map((cell) => {
									return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

const ResidentDetails = ({ details, close, tableColumns }) => {
	// eslint-disable-next-line
	const columns = useMemo(() => [
		{
			Header: "Last Name",
			accessor: "lastName",
			disableSortBy: true,
		},
		{
			Header: "First Name",
			accessor: "firstName",
			disableSortBy: true,
		},
		{
			Header: "MIddle Initial",
			accessor: "middleInitial",
			disableSortBy: true,
		},
		{
			Header: "suffix",
			accessor: "suffix",
			disableSortBy: true,
		},
		{
			Header: "DOB",
			accessor: "DOB",
			disableSortBy: true,
			Cell: ({ value }) => <div>{toDisplayTime(value)}</div>,
		},
		{
			Header: "Type",
			accessor: "type",
			disableSortBy: true,
			Cell: ({ row }) => (
				<div>
					{row.original.type === "transfer"
						? row.original.transferType
							.replace(/([A-Z])/g, " $1")
							// uppercase the first character
							.replace(/^./, function (str) {
								return str.toUpperCase();
							})
						: row.original.type.replace(/^./, function (str) {
							return str.toUpperCase();
						})}
				</div>
			),
		},
		{
			Header: "Date of ADT",
			accessor: "dateOfADT",
			disableSortBy: true,
			Cell: ({ row }) => <div>{toDisplayTime(row.original.dateOfADT)}</div>,
		},
		{
			Header: "Dx",
			accessor: "dx",
			disableSortBy: true,
			Cell: ({ value }) => {
				if (value && Array.isArray(value)) {
					return <div>{value.map(v => v.label).join(", ")}</div>;
				}
				return <div>{value?.label || "-"}</div>;
			}
		},
		{
			Header: "Doctor",
			accessor: "doctor",
			disableSortBy: true,
			Cell: ({ value }) => <div>{value?.label || "-"}</div>,
		},
		{
			Header: "Nurse",
			accessor: "nurse",
			disableSortBy: true,
			Cell: ({ value }) => <div>{value?.label || "-"}</div>,
		},
		{
			Header: "Unit",
			accessor: "unit",
			disableSortBy: true,
			Cell: ({ value }) => <div>{value?.label || "-"}</div>,
		},
		{
			Header: "Insurance",
			accessor: "insurance",
			disableSortBy: true,
			Cell: ({ row }) => {
				const type = row.original.type;
				const isAdmissionOrReadmission = type === "admission" || type === "readmission";
				const insuranceObj = isAdmissionOrReadmission 
					? row.original.payerSourceInsurance 
					: row.original.insurance;
				return <div>{insuranceObj?.label || "-"}</div>;
			}
		},
		{
			Header: "Notes",
			accessor: "notes",
			disableSortBy: true,
			Cell: ({ value }) => {
				if (!value) return <div>-</div>;
				const count = 25;
				const isLong = value.length > count;
				const tooltipContent = isLong ? `${value.substring(0, count)}...` : value;

				if (isLong) {
					const tooltipTitle = (
						<CustomTooltipContentWithAction
							text={value}
						/>
					);
					const tooltipProps = {
						title: tooltipTitle,
						arrow: true,
						componentsProps: richTooltipSlotProps,
					};

					return (
						<HtmlTooltip PopperProps={{ style: { zIndex: 1500 } }} {...tooltipProps} disableFocusListener>
							<span style={{ cursor: "pointer", textDecoration: "underline rgba(0,0,0,0.2) dashed" }}>
								{tooltipContent}
							</span>
						</HtmlTooltip>
					);
				}
				return <div>{value}</div>;
			},
		},
	]);

	const [list, setList] = useState([]);

	const getUser = async () => {
		let data = await axios.post(
			"/api/patient/details-relations",
			{
				...details,
			},
			{ headers: { "x-auth": `${localStorage.getItem("x-auth")}` } }
		);

		setList(data.data || []);
	};

	useEffect(() => {
		getUser();
		// eslint-disable-next-line
	}, []);

	const columnsToDisplay = useMemo(() => {
		const baseColumns = columns; // tableColumns ? tableColumns : columns;
		return baseColumns.filter(
			(col) =>
				!["lastName", "firstName", "middleInitial", "suffix", "DOB"].includes(
					col.accessor
				)
		);
	}, [columns]);

	const initials = useMemo(() => {
		const first = details.firstName?.charAt(0) || "";
		const last = details.lastName?.charAt(0) || "";
		return `${first}${last}`.toUpperCase();
	}, [details]);

	const age = useMemo(() => {
		if (!details.DOB) return "-";
		const birthDate = new Date(details.DOB);
		const today = new Date();
		let calculatedAge = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			calculatedAge--;
		}
		return calculatedAge;
	}, [details.DOB]);

	return (
		<div className={styles.residentDetailsWrpr}>
			<div className={`${styles.residentDetailsContent}`}>
				<div className={`df aic ${styles.hdr}`}>
					<div className="df aic">
						<p className={`df aic ffmar fs16 fw700 ${styles.titleText}`}>
							Resident Details: {details.lastName}, {details.firstName} {details.middleInitial} {details.suffix}
						</p>
					</div>
					<div className={`df acc mla ${styles.closeBtn}`} onClick={close}>
						<NewCloseSVG />
					</div>
				</div>

				<div className={styles.patientCard}>
					<div className={styles.avatar}>{initials}</div>
					<div className={styles.nameSection}>
						{details.lastName}, {details.firstName} {details.middleInitial} {details.suffix}
					</div>
					<div className={styles.metaSection}>
						<span><strong>DOB:</strong> {details.DOB ? toDisplayTime(details.DOB, "MM/DD/YYYY") : "-"}</span>
						<span><strong>Age:</strong> {age}</span>
						<span><strong>Total Records:</strong> {list.length}</span>
						{details.facilityName && (
							<span><strong>Facility:</strong> {details.facilityName}</span>
						)}
					</div>
				</div>

				<div className={`${styles.tableSec}`}>
					<Table columns={columnsToDisplay} data={list} />
				</div>
			</div>
		</div>
	);
};

export default ResidentDetails;
